/**
 * White-label color theming (spec v2 §38/§41 "Use my colors").
 *
 * The design system's primary palette is a hand-tuned 10-step OKLCH ramp
 * whose LIGHTNESS steps carry the AA-contrast guarantees (see index.css).
 * A brand color therefore never replaces the ramp — it only rotates the HUE
 * and scales the CHROMA proportionally to the brand color's own chroma, so a
 * vivid brand stays vivid, a muted brand stays muted, and every derived
 * token (hover tints, focus rings, dark mode) keeps its designed contrast.
 */

export interface IOklch {
    /** Lightness 0–1. */
    l: number;
    /** Chroma (unbounded, ~0–0.4 for sRGB). */
    c: number;
    /** Hue in degrees 0–360. */
    h: number;
}

/** (lightness %, chroma) pairs copied from index.css — keep in sync. */
const LIGHT_RAMP: ReadonlyArray<readonly [number, number]> = [
    [97.1, 0.014],
    [94.1, 0.028],
    [88.3, 0.055],
    [79.4, 0.097],
    [65.7, 0.161],
    [51.1, 0.23],
    [45.7, 0.215],
    [39.1, 0.167],
    [32.6, 0.12],
    [26.7, 0.077],
];
const DARK_RAMP: ReadonlyArray<readonly [number, number]> = [
    [24, 0.03],
    [28, 0.042],
    [34, 0.058],
    [46, 0.1],
    [52, 0.16],
    [56, 0.207],
    [61, 0.185],
    [68, 0.15],
    [76, 0.11],
    [84, 0.07],
];
const STEPS = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900] as const;
/** The reference chroma at step 500 the ramps were designed around. */
const REFERENCE_CHROMA = 0.23;

export function hexToOklch(hex: string): IOklch | null {
    const match = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(hex.trim());
    if (!match) return null;
    let raw = match[1]!;
    if (raw.length === 3) raw = [...raw].map((ch) => ch + ch).join('');

    const srgb = [0, 1, 2].map((i) => parseInt(raw.slice(i * 2, i * 2 + 2), 16) / 255);
    const [r, g, b] = srgb.map((channel) =>
        channel <= 0.04045 ? channel / 12.92 : Math.pow((channel + 0.055) / 1.055, 2.4),
    ) as [number, number, number];

    const l = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b);
    const m = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b);
    const s = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b);

    const okL = 0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s;
    const okA = 1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s;
    const okB = 0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s;

    const c = Math.sqrt(okA * okA + okB * okB);
    let h = (Math.atan2(okB, okA) * 180) / Math.PI;
    if (h < 0) h += 360;
    return { l: okL, c, h };
}

function rampCss(
    ramp: ReadonlyArray<readonly [number, number]>,
    hue: number,
    chromaFactor: number,
): string {
    return STEPS.map((step, index) => {
        const [lightness, chroma] = ramp[index]!;
        const scaled = Math.round(chroma * chromaFactor * 1000) / 1000;
        return `--color-primary-${step}: oklch(${lightness}% ${scaled} ${Math.round(hue * 10) / 10});`;
    }).join('\n    ');
}

/**
 * Full stylesheet text overriding the primary scale in light AND dark mode.
 * Returns null for unparseable or near-achromatic colors (a gray brand would
 * erase every affordance the primary hue carries).
 */
export function buildBrandThemeCss(hex: string): string | null {
    const brand = hexToOklch(hex);
    if (!brand || brand.c < 0.02) return null;

    const chromaFactor = Math.min(1.3, Math.max(0.25, brand.c / REFERENCE_CHROMA));
    return `:root {\n    ${rampCss(LIGHT_RAMP, brand.h, chromaFactor)}\n}\n.dark {\n    ${rampCss(DARK_RAMP, brand.h, chromaFactor)}\n}`;
}
